import type { Breakpoint } from '@mui/material/styles';
import type { NavItemProps, NavSectionProps } from 'src/components/nav-section';
import type { MainSectionProps, HeaderSectionProps, LayoutSectionProps } from '../core';

import { merge } from 'es-toolkit';
import { useBoolean } from 'minimal-shared/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import { iconButtonClasses } from '@mui/material/IconButton';

import { isServerError } from 'src/lib/errors';

import { Logo } from 'src/components/logo';
import { useSettingsContext } from 'src/components/settings';
import { AppUpdatesPopover } from 'src/components/app-updates/app-updates-popover';

import { View500 } from 'src/sections/error';

import { NavMobile } from './nav-mobile';
import { VerticalDivider } from './content';
import { hasRoles } from '../../auth/utils';
import { NavVertical } from './nav-vertical';
import { useTranslate } from '../../locales';
import { NavHorizontal } from './nav-horizontal';
import { Role } from '../../enums/auth/role.enum';
import { EXTERNAL_LINKS } from '../../const/links';
import { useNavData } from '../nav-config-dashboard';
import { MenuButton } from '../components/menu-button';
import { AccountPopover } from '../components/account-drawer';
import { LanguagePopover } from '../components/language-popover';
import { AdminRolesSwitcher } from '../components/admin-roles-switcher';
import { dashboardLayoutVars, dashboardNavColorVars } from './css-vars';
import { useAuthContext, useAvailableAdminRoles } from '../../auth/hooks';
import { MainSection, HeaderSection, layoutClasses, LayoutSection } from '../core';

type LayoutBaseProps = Pick<LayoutSectionProps, 'sx' | 'children' | 'cssVars'>;

export type DashboardLayoutProps = LayoutBaseProps & {
  layoutQuery?: Breakpoint;
  slotProps?: {
    header?: HeaderSectionProps;
    nav?: {
      data?: NavSectionProps['data'];
    };
    main?: MainSectionProps;
  };
};

export function DashboardLayout({
  sx,
  cssVars,
  children,
  slotProps,
  layoutQuery = 'lg',
}: DashboardLayoutProps) {
  const theme = useTheme();
  const { t, currentLang } = useTranslate('navbar');

  const { user, assumedRoleId } = useAuthContext();
  const { adminRoles, adminRolesLoading } = useAvailableAdminRoles();

  const settings = useSettingsContext();

  const navVars = dashboardNavColorVars(theme, settings.state.navColor, settings.state.navLayout);

  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();

  const { data: dashboardNavData, error: navError } = useNavData();

  const navData = slotProps?.nav?.data ?? dashboardNavData;

  const isNavMini = settings.state.navLayout === 'mini';
  const isNavHorizontal = settings.state.navLayout === 'horizontal';
  const isNavVertical = isNavMini || settings.state.navLayout === 'vertical';

  const canDisplayItemByRole = (allowedRoles: NavItemProps['allowedRoles']): boolean => {
    const currentRoles: Role[] = user?.authorities || [];
    if (assumedRoleId) {
      currentRoles.push(Role.Admin);
    }
    return hasRoles(currentRoles, allowedRoles);
  };

  const renderBackToDashboardButton = () =>
    !adminRolesLoading &&
    adminRoles.length > 0 &&
    assumedRoleId && (
      <Box sx={{ py: 1, px: 4 }}>
        <Button
          component="a"
          href={EXTERNAL_LINKS.app.administration_with_role(assumedRoleId)}
          variant="outlined"
          rel="noopener noreferrer"
          fullWidth
          startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
        >
          {t('legacyAdmin')}
        </Button>
      </Box>
    );

  const renderBottomArea = () => (
    <Box sx={{ px: 4, py: 2 }}>
      <Button
        component="a"
        href={EXTERNAL_LINKS.website.support(currentLang.value)}
        variant="outlined"
        rel="noopener noreferrer"
        target="_blank"
        fullWidth
        startIcon={<FontAwesomeIcon icon={faCircleQuestion} />}
      >
        {t('support')}
      </Button>
    </Box>
  );

  const renderHeader = () => {
    const headerSlotProps: HeaderSectionProps['slotProps'] = {
      container: {
        maxWidth: false,
        sx: {
          ...(isNavVertical && { px: { [layoutQuery]: 5 } }),
          ...(isNavHorizontal && {
            bgcolor: 'var(--layout-nav-bg)',
            height: { [layoutQuery]: 'var(--layout-nav-horizontal-height)' },
            [`& .${iconButtonClasses.root}`]: { color: 'var(--layout-nav-text-secondary-color)' },
          }),
        },
      },
    };

    const headerSlots: HeaderSectionProps['slots'] = {
      topArea: (
        <Alert severity="info" sx={{ display: 'none', borderRadius: 0 }}>
          This is an info Alert.
        </Alert>
      ),
      bottomArea: isNavHorizontal ? (
        <NavHorizontal
          data={navData}
          layoutQuery={layoutQuery}
          cssVars={navVars.section}
          checkPermissions={canDisplayItemByRole}
        />
      ) : null,
      leftArea: (
        <>
          {/** @slot Nav mobile */}
          <MenuButton
            onClick={onOpen}
            sx={{ mr: 1, ml: -1, [theme.breakpoints.up(layoutQuery)]: { display: 'none' } }}
          />
          <NavMobile
            data={navData}
            open={open}
            onClose={onClose}
            cssVars={navVars.section}
            checkPermissions={canDisplayItemByRole}
            slots={{
              aboveNavArea: renderBackToDashboardButton(),
              bottomArea: renderBottomArea(),
            }}
          />

          {/** @slot Logo */}
          {isNavHorizontal && (
            <Logo
              sx={{
                display: 'none',
                [theme.breakpoints.up(layoutQuery)]: { display: 'inline-flex' },
              }}
            />
          )}

          {/** @slot Divider */}
          {isNavHorizontal && (
            <VerticalDivider sx={{ [theme.breakpoints.up(layoutQuery)]: { display: 'flex' } }} />
          )}

          {/** @slot Admin roles switcher */}
          {!adminRolesLoading && adminRoles.length > 0 && (
            <AdminRolesSwitcher
              sx={{ ...(isNavHorizontal && { color: 'var(--layout-nav-text-primary-color)' }) }}
            />
          )}
        </>
      ),
      rightArea: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0, sm: 0.75 } }}>
          {/** @slot App Updates popover */}
          <AppUpdatesPopover />

          {/** @slot Language popover */}
          <LanguagePopover
            data={[
              { value: 'de', label: 'German', countryCode: 'DE' },
              { value: 'en', label: 'English', countryCode: 'GB' },
              { value: 'fr', label: 'French', countryCode: 'FR' },
            ]}
          />

          {/** @slot Account popover */}
          <AccountPopover />
        </Box>
      ),
    };

    return (
      <HeaderSection
        layoutQuery={layoutQuery}
        disableElevation={isNavVertical}
        {...slotProps?.header}
        slots={{ ...headerSlots, ...slotProps?.header?.slots }}
        slotProps={merge(headerSlotProps, slotProps?.header?.slotProps ?? {})}
        sx={slotProps?.header?.sx}
      />
    );
  };

  const renderSidebar = () => (
    <NavVertical
      data={navData}
      isNavMini={isNavMini}
      layoutQuery={layoutQuery}
      cssVars={navVars.section}
      checkPermissions={canDisplayItemByRole}
      onToggleNav={() => {}}
      slots={{
        aboveNavArea: renderBackToDashboardButton(),
        bottomArea: renderBottomArea(),
      }}
    />
  );

  const renderFooter = () => null;

  const renderMain = () => (
    <MainSection {...slotProps?.main}>
      {isServerError(navError) ? <View500 /> : children}
    </MainSection>
  );

  return (
    <LayoutSection
      /** **************************************
       * @Header
       *************************************** */
      headerSection={renderHeader()}
      /** **************************************
       * @Sidebar
       *************************************** */
      sidebarSection={isNavHorizontal ? null : renderSidebar()}
      /** **************************************
       * @Footer
       *************************************** */
      footerSection={renderFooter()}
      /** **************************************
       * @Styles
       *************************************** */
      cssVars={{ ...dashboardLayoutVars(theme), ...navVars.layout, ...cssVars }}
      sx={[
        {
          [`& .${layoutClasses.sidebarContainer}`]: {
            [theme.breakpoints.up(layoutQuery)]: {
              pl: isNavMini ? 'var(--layout-nav-mini-width)' : 'var(--layout-nav-vertical-width)',
              transition: theme.transitions.create(['padding-left'], {
                easing: 'var(--layout-transition-easing)',
                duration: 'var(--layout-transition-duration)',
              }),
            },
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {renderMain()}
    </LayoutSection>
  );
}
